if (!customElements.get('ss-collection-slot-grid')) {
  customElements.define('ss-collection-slot-grid', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      this.grid = this.querySelector('#ProductsList');
      this.button = this.querySelector('[data-slot-more]');
      this.nextLink = this.querySelector('[data-slot-next]');
      this.controls = this.querySelector('[data-slot-controls]');
      this.error = this.querySelector('[data-slot-error]');
      if (!this.isConnected || !this.grid || !this.button) return;
      if (this.dataset.mobilePagination === 'number') {
        if (!this.paginationMedia) {
          this.paginationMedia = matchMedia('(max-width: 767.98px)');
          // Re-enter the other mode from its URL, without mixing page models.
          this.onPaginationModeChange = () => window.location.reload();
          this.paginationMedia.addEventListener('change', this.onPaginationModeChange);
        }
        if (this.paginationMedia.matches) {
          // Without Sale grouping, Shopify's requested page is already final.
          if (this.dataset.saleLast !== 'true') return;
          if (!this.mobileInitialized) {
            this.mobileInitialized = true;
            this.mobileRender = this.loadMobilePage();
          }
          return;
        }
      }
      if (!this.initialized) {
        const initialItems = Array.from(this.grid.children);
        this.pending = [];
        this.pendingSourceURLs = new WeakMap();
        this.nextURL = this.dataset.nextUrl || '';
        this.saleLast = this.dataset.saleLast === 'true';
        this.salePending = [];
        this.bannerPending = [];
        this.rankedProducts = 0;
        this.seenProducts = new Set();
        this.seenPages = new Set();
        const currentPage = Math.max(1, Number(this.dataset.currentPage) || 1);
        if (this.saleLast && currentPage > 1) {
          // A direct page link must rebuild the earlier groups too; otherwise
          // sale cards deferred on preceding Shopify pages would be lost.
          const firstURL = new URL(this.nextURL || this.dataset.previousUrl || location.href, location.href);
          firstURL.searchParams.set('page', '1');
          firstURL.searchParams.delete('section_id');
          this.nextURL = firstURL.href;
          this.initialBatches = currentPage;
        } else {
          this.enqueue(initialItems);
        }
        this.grid.replaceChildren();
        this.initialized = true;
      }
      this.onClick = event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.load();
      };
      this.button.addEventListener('click', this.onClick);
      this.nextLink?.addEventListener('click', this.onClick);
      if (this.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.load();
        }, { rootMargin: '200px' });
      }
      if (!this.loading) {
        this.button.disabled = false;
        this.button.classList.remove('btn--loading');
        this.setLinkLoading(false);
        this.removeAttribute('aria-busy');
      }
      this.syncControls();
      if (!this.controls.hidden && this.error.hidden) this.intersection?.observe(this.visibleControl());
      this.columns = this.readColumns();
      this.load(this.grid.children.length > 0);
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => {
          const columns = this.readColumns();
          if (columns !== this.columns) {
            this.columns = columns;
            if (this.loading) this.fillAfterLoad = true;
            else this.load(true);
          }
        });
        this.resize.observe(this.grid);
      }
    }

    readColumns() {
      return Math.max(1, parseInt(getComputedStyle(this.grid).getPropertyValue('--collection-columns'), 10) || Number(this.dataset.columns) || 4);
    }

    async loadMobilePage() {
      const navigation = this.querySelector('[data-mobile-slot-pagination]');
      const error = this.querySelector('[data-mobile-page-error]');
      if (!navigation || !error) return;
      const retry = this.querySelector('[data-mobile-page-retry]');
      if (retry) retry.href = location.href;
      // Shopify renders the page links in the HTML, so show pagination
      // immediately while the mobile Sale-last page is reconstructed.
      error.hidden = true;
      const sourceProducts = Array.from(this.grid.children).filter(item => item.classList.contains('product-column'));
      // A final source page containing only Sale already is the stable tail
      // of the globally grouped order. No preceding pages are needed.
      if (!this.dataset.nextUrl && sourceProducts.length && sourceProducts.every(item => item.dataset.slotSale === 'true')) {
        this.mobileHasRendered = true;
        return;
      }
      this.setAttribute('aria-busy', 'true');
      const targetPage = Math.max(1, parseInt(this.dataset.currentPage, 10) || 1);
      // Match Shopify's native pagination: banners do not consume product slots.
      const productsPerPage = Math.max(1, Number(this.dataset.slots) || 20);
      this.pending = [];
      this.pendingSourceURLs = new WeakMap();
      this.salePending = [];
      this.bannerPending = [];
      this.rankedProducts = 0;
      this.seenProducts = new Set();
      this.seenPages = new Set();
      this.saleLast = this.dataset.saleLast === 'true';
      this.nextURL = this.dataset.nextUrl || '';
      if (targetPage === 1 && !this.mobileHasRendered) {
        this.enqueue(Array.from(this.grid.children));
      } else {
        // Sale grouping changes the visible product order.
        // Reconstruct preceding pages off-DOM.
        const firstURL = new URL(this.dataset.previousUrl || this.nextURL || location.href, location.href);
        firstURL.searchParams.set('page', '1');
        firstURL.searchParams.delete('section_id');
        this.nextURL = firstURL.href;
      }
      try {
        let visibleItems = [];
        for (let page = 1; page <= targetPage; page++) {
          const items = [];
          let products = 0;
          while (this.isConnected && products < productsPerPage) {
            while (this.pending.length && products < productsPerPage) {
              const item = this.pending.shift();
              items.push(item);
              if (item.classList.contains('product-column')) products++;
            }
            if (products >= productsPerPage || !this.nextURL) break;
            await this.fetchNext();
          }
          if (!this.isConnected) return;
          // Keep any trailing banners on the final product page.
          if (!this.nextURL && !this.pending.some(item => item.classList.contains('product-column'))) {
            items.push(...this.pending);
            this.pending = [];
          }
          visibleItems = items;
          if (!this.nextURL && !this.pending.length) break;
        }
        this.grid.replaceChildren(...visibleItems);
        this.mobileHasRendered = true;
        if (visibleItems.length) this.closest('#ProductGridContainer')?.querySelector('.empty-state')?.remove();
        // Release all off-DOM queues after constructing the requested page.
        this.pending = [];
        this.salePending = [];
        this.bannerPending = [];
        this.seenProducts.clear();
        this.seenPages.clear();
      } catch (failure) {
        // Leave the server-rendered products available on a failed request.
        if (this.isConnected) error.hidden = false;
      } finally {
        if (this.isConnected) this.removeAttribute('aria-busy');
      }
    }

    visibleControl() {
      return this.nextLink && !this.nextLink.hidden ? this.nextLink : this.button;
    }

    setLinkLoading(active) {
      if (!this.nextLink) return;
      this.nextLink.classList.toggle('btn--loading', active);
      if (active) this.nextLink.setAttribute('aria-disabled', 'true');
      else this.nextLink.removeAttribute('aria-disabled');
    }

    syncControls() {
      this.controls.hidden = !this.nextURL && !this.pending.length && !this.salePending?.length;
      if (this.saleLast) {
        // A source page number cannot address a mixture of two sorted groups.
        // Use a real button after JS initializes; retain the native link as
        // the no-JavaScript fallback in the server-rendered markup.
        if (this.nextLink) this.nextLink.hidden = true;
        this.button.hidden = this.controls.hidden;
        return;
      }
      if (!this.nextLink) return;
      // A fetched page may still contain buffered products. Link to that page
      // before advancing, so opening a new tab does not skip those products.
      const pendingURL = this.pending.map(item => this.pendingSourceURLs?.get(item)).find(Boolean);
      const nextURL = pendingURL || this.nextURL;
      if (nextURL) this.nextLink.setAttribute('href', nextURL);
      else this.nextLink.removeAttribute('href');
      this.nextLink.hidden = !nextURL || this.controls.hidden;
      // A last partial row can contain buffered items but no next Shopify page.
      this.button.hidden = !!nextURL || this.controls.hidden;
    }

    span(item) {
      return item.classList.contains('image-column--wide') ? 2 : 1;
    }

    layout(columns) {
      const rows = [];
      for (const item of this.grid.children) {
        const span = this.span(item);
        let row = rows.findIndex(space => space >= span);
        if (row === -1) {
          row = rows.length;
          rows.push(columns);
        }
        rows[row] -= span;
      }
      return rows;
    }

    plan(availableRows) {
      const rows = [...availableRows];
      const selected = [];
      const remaining = [];
      // Match CSS dense placement: a product can fill the gap before a wide banner.
      for (const item of this.pending) {
        const span = this.span(item);
        const row = rows.findIndex(space => space >= span);
        if (row !== -1) {
          selected.push(item);
          rows[row] -= span;
        } else {
          remaining.push(item);
        }
      }
      return { selected, remaining, rows, full: rows.every(space => space === 0) };
    }

    async load(completeRowOnly = false) {
      if (this.saleLast) return this.loadSaleLast(completeRowOnly);
      if (!this.isConnected || this.loading) return;
      const columns = this.readColumns();
      const rows = this.layout(columns);
      if (!completeRowOnly) {
        const pageRows = Math.max(1, Math.floor((Number(this.dataset.slots) || 20) / columns));
        rows.push(...Array(pageRows).fill(columns));
      }
      if (rows.every(space => space === 0)) return;
      this.loading = true;
      this.intersection?.disconnect();
      this.button.disabled = true;
      this.button.classList.add('btn--loading');
      this.setLinkLoading(true);
      this.setAttribute('aria-busy', 'true');
      this.error.hidden = true;

      try {
        let plan = this.plan(rows);
        while (!plan.full && this.nextURL) {
          await this.fetchNext();
          if (!this.isConnected) return;
          plan = this.plan(rows);
        }
        if (!this.nextURL && !plan.full) {
          // At the end of the collection, show everything that remains.
          plan.selected = this.pending;
          plan.remaining = [];
        }
        this.grid.append(...plan.selected);
        this.pending = plan.remaining;
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.error.hidden = false;
      } finally {
        this.loading = false;
        if (this.isConnected) {
          this.button.disabled = false;
          this.button.classList.remove('btn--loading');
          this.setLinkLoading(false);
          this.removeAttribute('aria-busy');
          this.syncControls();
          // On an error, stop automatic retries; the button still works.
          if (!this.controls.hidden && this.error.hidden) this.intersection?.observe(this.visibleControl());
          if (this.fillAfterLoad) {
            this.fillAfterLoad = false;
            if (this.error.hidden) this.load(true);
          }
        }
      }
    }

    enqueue(items, sourceURL) {
      if (!this.saleLast) {
        items.forEach(item => {
          if (sourceURL) this.pendingSourceURLs.set(item, sourceURL);
        });
        this.pending.push(...items);
        return;
      }

      // Register all banners before ranking this page's regular products.
      // Positions refer to the final product order, independently of Sale.
      this.bannerPending.push(...items.filter(item => item.classList.contains('image-column')));
      this.bannerPending.sort((a, b) => Number(a.dataset.slotProductPosition) - Number(b.dataset.slotProductPosition));
      for (const item of items) {
        if (!item.classList.contains('product-column')) continue;
        const id = item.dataset.slotProductId;
        if (id && this.seenProducts.has(id)) continue;
        if (id) this.seenProducts.add(id);
        if (item.dataset.slotSale === 'true') this.salePending.push(item);
        else this.enqueueRankedProduct(item);
      }
      // Only release Sale after the LAST Shopify source page was read.
      // The two queues preserve whichever native sort the visitor chose.
      if (!this.nextURL) {
        this.salePending.forEach(item => this.enqueueRankedProduct(item));
        this.salePending = [];
        this.pending.push(...this.bannerPending);
        this.bannerPending = [];
      }
    }

    enqueueRankedProduct(item) {
      this.rankedProducts += 1;
      while (this.bannerPending.length && Number(this.bannerPending[0].dataset.slotProductPosition) <= this.rankedProducts) {
        this.pending.push(this.bannerPending.shift());
      }
      this.pending.push(item);
    }

    async loadSaleLast(completeRowOnly = false) {
      if (!this.isConnected || this.loading) return;
      const columns = this.readColumns();
      let rows = this.layout(columns);
      if (!completeRowOnly) {
        const pageRows = Math.max(1, Math.floor((Number(this.dataset.slots) || 20) / columns));
        rows.push(...Array(pageRows * (this.initialBatches || 1)).fill(columns));
        this.initialBatches = 0;
      }
      if (rows.every(space => space === 0)) return;

      this.loading = true;
      this.intersection?.disconnect();
      this.button.disabled = true;
      this.button.classList.add('btn--loading');
      this.setAttribute('aria-busy', 'true');
      this.error.hidden = true;
      this.controls.hidden = false;
      try {
        while (this.isConnected) {
          const plan = this.plan(rows);
          // Display regular products as they arrive, even if scanning past
          // several pages of Sale is necessary to finish the visible rows.
          this.grid.append(...plan.selected);
          this.pending = plan.remaining;
          rows = plan.rows;
          if (plan.full || !this.nextURL) break;
          await this.fetchNext();
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.error.hidden = false;
      } finally {
        this.loading = false;
        if (this.isConnected) {
          this.button.disabled = false;
          this.button.classList.remove('btn--loading');
          this.removeAttribute('aria-busy');
          this.syncControls();
          if (!this.controls.hidden && this.error.hidden) this.intersection?.observe(this.visibleControl());
          if (this.fillAfterLoad) {
            this.fillAfterLoad = false;
            if (this.error.hidden) this.load(true);
          }
        }
      }
    }

    async fetchNext() {
      const url = new URL(this.nextURL, window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      if (this.saleLast && this.seenPages.has(url.href)) throw new Error('Collection pagination repeated a page');
      this.request = new AbortController();
      const request = this.request;
      const timeout = setTimeout(() => request.abort(), 20000);
      let response;
      let html;
      try {
        response = await fetch(url.toString(), { signal: request.signal });
        if (!response.ok) throw new Error('Collection request failed');
        html = await response.text();
      } catch (error) {
        if (request.signal.aborted && this.isConnected) throw new Error('Collection request timed out');
        throw error;
      } finally {
        clearTimeout(timeout);
      }
      if (!response.ok) throw new Error('Collection request failed');
      const document = new DOMParser().parseFromString(html, 'text/html');
      if (this.request !== request || request.signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
      if (!this.isConnected) return;
      const source = document.querySelector('ss-collection-slot-grid');
      const grid = source?.querySelector('#ProductsList');
      if (!grid || source.dataset.sectionId !== this.dataset.sectionId) throw new Error('Collection grid missing');
      if (this.saleLast && (source.dataset.saleLast !== 'true' || source.dataset.collectionId !== this.dataset.collectionId)) {
        throw new Error('Collection source changed');
      }
      const nextURL = source.dataset.nextUrl || '';
      if (nextURL && Number(new URL(nextURL, window.location.href).searchParams.get('page')) <= Number(url.searchParams.get('page') || 1)) {
        throw new Error('Collection pagination did not advance');
      }
      const items = Array.from(grid.children);
      const pageURL = new URL(url);
      pageURL.searchParams.delete('section_id');
      this.pendingSourceURLs ||= new WeakMap();
      this.nextURL = nextURL;
      if (this.saleLast) this.seenPages.add(url.href);
      this.enqueue(items, pageURL.href);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.request?.abort();
      this.intersection?.disconnect();
      this.resize?.disconnect();
      this.button?.removeEventListener('click', this.onClick);
      this.nextLink?.removeEventListener('click', this.onClick);
      this.paginationMedia?.removeEventListener('change', this.onPaginationModeChange);
      this.paginationMedia = null;
      this.mobileInitialized = false;
    }
  });
}
